import { useQuery, QueryClient } from '@tanstack/react-query';
import { api } from '@/utils/api';
import { API_ENDPOINTS } from '@/constants/api';
import { PaginatedResponse, AccessRequest, ToolRequest, Tag, Client, PricingStatus, CostType } from '@/types/tool';
import { User } from '@/types/auth';
import { useAtomValue } from 'jotai';
import { authAtom } from '@/store/auth';
import { UserRole } from '@/constants/roles';

// Common cache configuration
const CACHE_CONFIG = {
  staleTime: 30 * 1000, // 30 seconds - reduced for more responsive updates
  gcTime: 2 * 60 * 1000, // 2 minutes - reduced
  refetchOnWindowFocus: true, // Enable for fresh data
  refetchOnMount: true, // Always get fresh data on mount
} as const;

const ADMIN_CACHE_CONFIG = {
  staleTime: 10 * 1000, // 10 seconds - admin data changes frequently
  gcTime: 1 * 60 * 1000, // 1 minute
  refetchOnWindowFocus: true,
  refetchOnMount: true,
} as const;

// Responsive cache for request-related data (shorter stale time for timely updates)
const REQUEST_CACHE_CONFIG = {
  staleTime: 10 * 1000, // 10 seconds
  gcTime: 60 * 1000, // 1 minute
  refetchOnWindowFocus: true,
  refetchOnMount: true,
} as const;

/**
 * Centralized query for admin pending access requests
 * This eliminates duplicate calls between AppLayout and AccessRequestList
 */
export function useAdminPendingRequests() {
  const { user } = useAtomValue(authAtom);
  
  return useQuery<PaginatedResponse<AccessRequest>>({
    queryKey: ['admin', 'accessRequests', 'pending'],
    queryFn: async () => {
      return api.get<PaginatedResponse<AccessRequest>>(
        `${API_ENDPOINTS.ACCESS.ADMIN_REQUESTS}?status=PENDING&limit=100`
      );
    },
    enabled: !!user && user.role === UserRole.ADMIN,
    retry: false, // Don't retry if user no longer has admin access
    ...ADMIN_CACHE_CONFIG,
  });
}

/**
 * Centralized query for pending tool requests
 * This eliminates duplicate calls between AppLayout and ToolRequestList
 */
export function usePendingToolRequests() {
  const { user } = useAtomValue(authAtom);
  
  return useQuery<ToolRequest[]>({
    queryKey: ['admin', 'toolRequests', 'pending'],
    queryFn: async () => {
      return api.get<ToolRequest[]>(API_ENDPOINTS.TOOLS.PENDING_TOOL_REQUESTS);
    },
    enabled: !!user && user.role === UserRole.ADMIN,
    retry: false, // Don't retry if user no longer has admin access
    ...ADMIN_CACHE_CONFIG,
  });
}

/**
 * Centralized query for approver pending requests
 */
export function useApproverPendingRequests() {
  const { user } = useAtomValue(authAtom);
  
  return useQuery<PaginatedResponse<AccessRequest>>({
    queryKey: ['approver', 'accessRequests', 'pending', user?.id],
    queryFn: async () => {
      return api.get<PaginatedResponse<AccessRequest>>(
        `${API_ENDPOINTS.ACCESS.APPROVER_REQUESTS}?status=PENDING&limit=1`
      );
    },
    enabled: !!user && user.isApprover,
    retry: false, // Don't retry if user no longer has approver access
    ...ADMIN_CACHE_CONFIG,
  });
}

/**
 * Centralized query for user access IDs
 * This eliminates potential duplicate calls across components
 */
export function useUserAccessIds() {
  const { user } = useAtomValue(authAtom);
  
  return useQuery<string[]>({
    queryKey: ['user', 'accessIds', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      return api.get<string[]>(API_ENDPOINTS.TOOLS.ACCESS_IDS);
    },
    enabled: !!user,
    ...REQUEST_CACHE_CONFIG, // Use ultra-responsive config for immediate updates
  });
}

/**
 * Centralized query for processing requests (admin)
 */
export function useProcessingRequests() {
  const { user } = useAtomValue(authAtom);
  
  return useQuery<PaginatedResponse<AccessRequest>>({
    queryKey: ['admin', 'accessRequests', 'processing'],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('status', 'PROCESSING');
      params.append('limit', '1000');
      return api.get<PaginatedResponse<AccessRequest>>(
        `${API_ENDPOINTS.ACCESS.ADMIN_REQUESTS}?${params.toString()}`
      );
    },
    enabled: !!user && user.role === UserRole.ADMIN,
    retry: false, // Don't retry if user no longer has admin access
    ...ADMIN_CACHE_CONFIG,
  });
}

/**
 * Centralized query for tags
 * This eliminates duplicate calls across TagFilterPanel, TagSelector, and Dashboard
 */
export function useTags() {
  return useQuery<{ tags: Tag[] }>({
    queryKey: ['tags'],
    queryFn: () => api.get<{ tags: Tag[] }>(API_ENDPOINTS.TOOLS.TAGS),
    ...CACHE_CONFIG,
  });
}

/**
 * Centralized query for user's requests
 */
export function useMyRequests() {
  const { user } = useAtomValue(authAtom);
  
  return useQuery<PaginatedResponse<AccessRequest>>({
    queryKey: ['user', 'requests', user?.id],
    queryFn: () => api.get<PaginatedResponse<AccessRequest>>(
      `${API_ENDPOINTS.ACCESS.MY_REQUESTS}?limit=1000`
    ),
    enabled: !!user,
    ...REQUEST_CACHE_CONFIG, // Use ultra-responsive config for immediate updates
  });
}

/**
 * Centralized query for user's tool requests
 */
export function useMyToolRequests() {
  const { user } = useAtomValue(authAtom);
  
  return useQuery<ToolRequest[]>({
    queryKey: ['user', 'toolRequests', user?.id],
    queryFn: () => api.get<ToolRequest[]>(API_ENDPOINTS.TOOLS.MY_TOOL_REQUESTS),
    enabled: !!user,
    ...REQUEST_CACHE_CONFIG, // Use ultra-responsive config for immediate updates
  });
}

/**
 * Standardized invalidation functions for common patterns
 */
export const invalidatePatterns = {
  adminRequests: () => ['admin', 'accessRequests'],
  approverRequests: () => ['approver', 'accessRequests'],
  userRequests: () => ['user', 'requests'],
  userToolRequests: () => ['user', 'toolRequests'],
  userAccess: () => ['user', 'accessIds'],
  toolRequests: () => ['admin', 'toolRequests'],
  tags: () => ['tags'],
  dashboardTools: () => ['dashboardRawTools'], // This will invalidate all queries that start with 'dashboardRawTools'
  tools: () => ['tools'],
  toolStats: () => ['toolStats'],
  tool: (toolId?: string) => toolId ? ['tool', toolId] : ['tool'],
  sharedCredentials: (toolId?: string) => toolId ? ['sharedCredentials', toolId] : ['sharedCredentials'],
  users: () => ['users'],
  userCosts: () => ['admin', 'userCosts'] as const,
  user: (userId?: string) => userId ? ['user', userId] : ['user'],
  // Real-time invalidation patterns for immediate UI updates
  realTimeRequests: () => [
    ['user', 'requests'],
    ['user', 'accessIds'],
    ['dashboardRawTools'],
    ['admin', 'accessRequests'],
    ['approver', 'accessRequests']
  ],
} as const;

/**
 * Helper function to perform comprehensive cache invalidation for request-related operations
 * This ensures immediate UI updates across all components
 */
export function invalidateRequestRelatedQueries(queryClient: QueryClient) {
  const patterns = [
    invalidatePatterns.userRequests(),
    invalidatePatterns.userToolRequests(),
    invalidatePatterns.userAccess(),
    invalidatePatterns.dashboardTools(),
    invalidatePatterns.adminRequests(),
    invalidatePatterns.approverRequests(),
    invalidatePatterns.toolRequests(),
  ];
  
  patterns.forEach(pattern => {
    queryClient.invalidateQueries({ queryKey: pattern });
  });
  
  // Also refetch any currently active queries to get immediate updates
  queryClient.refetchQueries({ 
    predicate: (query) => {
      const key = query.queryKey[0];
      return key === 'user' || key === 'dashboardRawTools' || key === 'admin' || key === 'approver';
    }
  });
}

/** Per-user cost rows from GET /user-management/users/costs */
export type UserCostRow = {
  userId: string;
  userName: string;
  email: string;
  totalCost: number;
  tools: {
    toolId: string;
    toolName: string;
    costPerUser: number | null;
    costType: CostType;
    flatFeeCost: number | null;
    pricingStatus: PricingStatus;
    totalCost: number | null;
  }[];
};

/**
 * Admin cost analysis: user-level roll-up (priced user-based tools; flat-fee shown without allocation)
 */
export function useUserCosts() {
  const { user } = useAtomValue(authAtom);

  return useQuery({
    queryKey: invalidatePatterns.userCosts(),
    queryFn: () =>
      api.get<UserCostRow[]>(API_ENDPOINTS.USERS.USER_COSTS),
    enabled: !!user && user.role === UserRole.ADMIN,
    ...ADMIN_CACHE_CONFIG,
  });
}

/**
 * Centralized query for client list (admin)
 */
export function useClients(search?: string, page = 1, limit = 20) {
  const { user } = useAtomValue(authAtom);

  return useQuery<PaginatedResponse<Client>>({
    queryKey: ['clients', { search, page, limit }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('page', String(page));
      params.set('limit', String(limit));
      return api.get(`${API_ENDPOINTS.CLIENTS.LIST}?${params.toString()}`);
    },
    enabled: !!user && user.role === UserRole.ADMIN,
    ...ADMIN_CACHE_CONFIG,
  });
}

/**
 * Helper function to refresh the current user's auth state when their data is updated
 * This ensures immediate role/permission updates without requiring re-login
 */
export async function refreshCurrentUserIfUpdated(
  queryClient: QueryClient, 
  updatedUserId: string, 
  currentUserId: string | undefined
) {
  // Only refresh if the updated user is the current user
  if (updatedUserId === currentUserId) {
    try {
      // Clear all role-dependent caches immediately to prevent stale data
      queryClient.removeQueries({ queryKey: ['admin'] });
      queryClient.removeQueries({ queryKey: ['approver'] });
      
      // Fetch fresh user data from the API
      const freshUserData = await api.get<User>(`/user-management/users/${updatedUserId}`);
      
      // Return fresh user data to be handled by the component that calls this function
      return freshUserData;
    } catch (error) {
      console.error('Failed to refresh current user data:', error);
      // If we can't refresh the user data, force a logout to prevent security issues
      window.location.href = '/login';
    }
  }
  return null;
} 